import {
    TextInput,
    Caption,
    useStyles,
    useAppDisplayModeContext,
    DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV,
    MOBILE_TOP_NAV_HEIGHT_FULL_LOGO,
    pxToRem,
} from '@archipro-design/aria';
import { TAGGING_SEARCH_MODAL_HEIGHT_PX } from '@modules/product-tagging/constant';
import { Loader, SearchLine } from '@archipro-design/icons';
import type { SyntheticEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import * as S from '@modules/product-tagging/components/product-tagging-search/ProductTaggingSearchModal.style';
import { useFetcher } from '@remix-run/react';
import type { loader } from '~/routes/remix-api.product-tagging.product-search';
import { ProductTaggingProductSearch } from '~/routes/remix-api.product-tagging.product-search';
import { useDebounceFn, useIsomorphicLayoutEffect } from 'ahooks';
import {
    URL_SEARCH_PARAM,
    MIN_PRODUCT_SEARCH_CHARACTERS,
} from '@modules/product-tagging/config/search-form';
import ProductSearchResultTile from '@modules/product-tagging/components/product-search-result-tile/ProductSearchResultTile';
import { Animation } from '@fluentui/react-northstar';
import type { ProductTaggingSearchFormProps } from '@modules/product-tagging/type';
import { useProductTagging } from '@modules/product-tagging/context';
import {
    TaggingModalShell,
    TaggingModalHeader,
    TaggingModalFieldUnderline,
    TaggingModalContent,
    TaggingModalFooterButton,
    TaggingModalLoadingOverlay,
} from '@modules/product-tagging/components/tagging-modal-shell/TaggingModalShell';

export const ProductSearchForm = ({
    onFormToggle,
    onConfirm,
    popperState,
    onDismiss,
}: ProductTaggingSearchFormProps) => {
    const { css } = useStyles();
    const [keyword, setKeyword] = useState('');
    const {
        state: { tagPosition },
        recentlyTaggedProducts,
        dispatch,
    } = useProductTagging();
    const ref = useRef<HTMLInputElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const { desktop } = useAppDisplayModeContext();

    const fetcher = useFetcher<typeof loader>();

    useIsomorphicLayoutEffect(() => {
        if (popperState?.rects && ref.current && modalRef.current) {
            ref.current.focus({ preventScroll: true });
            const rect = modalRef.current.getBoundingClientRect();
            const topOffset = desktop
                ? DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV
                : MOBILE_TOP_NAV_HEIGHT_FULL_LOGO;
            const isModalInView =
                rect &&
                rect.top >= topOffset &&
                rect.bottom <= window.innerHeight;
            if (!isModalInView) {
                modalRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [popperState, tagPosition, desktop]);

    const { run: searchProduct } = useDebounceFn(
        (event: SyntheticEvent<HTMLElement, Event>) => {
            const target = event.target as HTMLInputElement;
            setKeyword(target.value);
            if (target.value.length >= MIN_PRODUCT_SEARCH_CHARACTERS) {
                fetcher.load(
                    `${ProductTaggingProductSearch}?${URL_SEARCH_PARAM}=${target.value}`,
                );
            }
        },
        {
            wait: 300,
            leading: true,
        },
    );

    const initiateSearch = keyword.length >= MIN_PRODUCT_SEARCH_CHARACTERS;
    const productSearchResults = initiateSearch ? fetcher.data || [] : [];
    const loading = fetcher.state === 'loading';

    const [isConfirming, setIsConfirming] = useState(false);
    const handleConfirm = useCallback(
        (data: Parameters<typeof onConfirm>[0]) => {
            setIsConfirming(true);
            onConfirm(data);
        },
        [onConfirm],
    );

    return (
        <TaggingModalShell
            style={{ height: pxToRem(TAGGING_SEARCH_MODAL_HEIGHT_PX) }}
            onDismiss={() => {
                dispatch({ type: 'ADDING_TAG', payload: undefined });
                onDismiss?.();
            }}
        >
            <TaggingModalHeader
                ref={modalRef}
                style={{
                    scrollMarginTop: pxToRem(
                        desktop
                            ? DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV
                            : MOBILE_TOP_NAV_HEIGHT_FULL_LOGO,
                    ),
                }}
            >
                <div className="w-full">
                    <TextInput
                        className={css(S.SearchTextInput)}
                        input={{
                            placeholder: 'Search product or supplier',
                            ref,
                        }}
                        size={'md'}
                        suffixIcons={[<SearchLine key={'searchIcon'} />]}
                        onChange={searchProduct}
                    />
                    <TaggingModalFieldUnderline />
                </div>
            </TaggingModalHeader>

            <TaggingModalContent>
                {productSearchResults.length > 0 ? (
                    <ul aria-label="Search results">
                        {productSearchResults.map((tile) => (
                            <li key={tile.pType + tile.id}>
                                <ProductSearchResultTile
                                    tileData={tile}
                                    showProfessionalName={true}
                                    onConfirm={handleConfirm}
                                />
                            </li>
                        ))}
                    </ul>
                ) : (
                    <>
                        {!initiateSearch &&
                        recentlyTaggedProducts.length > 0 ? (
                            <>
                                <Caption
                                    className="ml-4 text-gray-500"
                                    variant="04"
                                >
                                    Recently tagged
                                </Caption>
                                <ul aria-label="Recently tagged">
                                    {recentlyTaggedProducts.map((tile) => (
                                        <li key={tile.pType + tile.id}>
                                            <ProductSearchResultTile
                                                tileData={tile}
                                                showProfessionalName={true}
                                                onConfirm={handleConfirm}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            <Caption
                                className="ml-4 text-gray-500"
                                variant="04"
                            >
                                No results
                            </Caption>
                        )}
                    </>
                )}
            </TaggingModalContent>

            <TaggingModalFooterButton
                label="Can't find product?"
                onClick={() => onFormToggle('supplier')}
            />

            {(loading || isConfirming) && (
                <TaggingModalLoadingOverlay>
                    <Animation
                        name="rotateUltraSlow"
                        className={css(S.TaggingSearchLoader)}
                    >
                        <Loader size="2x" />
                    </Animation>
                </TaggingModalLoadingOverlay>
            )}
        </TaggingModalShell>
    );
};
